import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useDceAnalysisHistory,
  useDceAnalysisHistoryDetail,
} from '../hooks/useDceAnalysis';
import { dceAnalysisService } from '../services/dce-analysis.service';
import { useQuery } from '@tanstack/react-query';
import type {
  DceAnalysisStatus,
  DceRequirement,
  DceProfile,
  DceRequiredDocument,
} from '../types/dce-analysis.types';

const STATUS_LABELS: Record<DceAnalysisStatus, string> = {
  PENDING: 'En attente',
  RUNNING: 'En cours',
  DONE: 'Terminée',
  PARTIAL_SUCCESS: 'Terminée (partielle)',
  FAILED: 'En échec',
};

const STATUS_COLORS: Record<DceAnalysisStatus, string> = {
  PENDING: 'bg-gray-100 text-gray-700 border-gray-300',
  RUNNING: 'bg-blue-100 text-blue-700 border-blue-300',
  DONE: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  PARTIAL_SUCCESS: 'bg-amber-100 text-amber-700 border-amber-300',
  FAILED: 'bg-red-100 text-red-700 border-red-300',
};

/**
 * Sprint 4.E — Page lecture seule d'une analyse historique (non courante).
 *
 * Route : `/tm/opportunities/:id/analysis/history/:analysisId`
 *
 * Affiche l'analyse + tous ses items avec leur statut au moment du
 * remplacement. Aucune action de validation/édition possible — c'est une
 * archive.
 */
export function DceAnalysisHistoryPage() {
  const { id, analysisId } = useParams<{ id: string; analysisId: string }>();
  const navigate = useNavigate();
  const opportunityId = id ? parseInt(id, 10) : undefined;
  const aid = analysisId ? parseInt(analysisId, 10) : undefined;

  const { data: analysis, isLoading } = useDceAnalysisHistoryDetail(opportunityId, aid);
  const { data: history } = useDceAnalysisHistory(opportunityId);

  // On charge les items via leurs endpoints actuels (qui retournent ceux de
  // l'analyse courante). Pour l'historique, on lit en bypass via une query
  // dédiée — ici on filtre côté client : si l'analyse est currente on a tout,
  // sinon on affiche un message explicatif.
  const isCurrent = analysis?.isCurrent ?? false;

  const { data: requirements } = useQuery({
    queryKey: ['tm', 'dce-analysis', 'history', aid, 'requirements'],
    queryFn: () => dceAnalysisService.listRequirements(opportunityId as number, {}),
    enabled: !!opportunityId && !!aid && isCurrent,
  });
  const { data: profiles } = useQuery({
    queryKey: ['tm', 'dce-analysis', 'history', aid, 'profiles'],
    queryFn: () => dceAnalysisService.listProfiles(opportunityId as number),
    enabled: !!opportunityId && !!aid && isCurrent,
  });
  const { data: requiredDocs } = useQuery({
    queryKey: ['tm', 'dce-analysis', 'history', aid, 'required-documents'],
    queryFn: () => dceAnalysisService.listRequiredDocuments(opportunityId as number),
    enabled: !!opportunityId && !!aid && isCurrent,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-copper" />
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive mb-4">Analyse introuvable</p>
        <Button onClick={() => navigate(`/tm/opportunities/${opportunityId}`)}>
          Retour à l'opportunité
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Link
        to={`/tm/opportunities/${opportunityId}`}
        className="inline-flex items-center gap-1 text-sm text-copper hover:underline"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Retour à l'opportunité
      </Link>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle>
              Analyse archivée du {new Date(analysis.createdAt).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={STATUS_COLORS[analysis.status]}>
                {STATUS_LABELS[analysis.status]}
              </Badge>
              {!analysis.isCurrent && (
                <Badge variant="outline" className="bg-gray-100 text-gray-700 border-gray-300">
                  Archivée
                </Badge>
              )}
              {analysis.isCurrent && (
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300">
                  Courante
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="text-sm space-y-1">
          <div>
            <span className="text-muted-foreground">Modèle IA : </span>
            <span className="font-medium">{analysis.modelUsed ?? '—'}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Déclenchée : </span>
            <span className="font-medium">{analysis.triggeredBy === 'MANUAL' ? 'Manuellement' : 'Automatiquement'}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Durée : </span>
            <span className="font-medium tabular-nums">
              {analysis.durationMs ? `${(analysis.durationMs / 1000).toFixed(1)} s` : '—'}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Coût IA : </span>
            <span className="font-medium tabular-nums">${analysis.totalCostUsd.toFixed(4)}</span>
          </div>
          {analysis.migratedItemsCount > 0 && (
            <div>
              <span className="text-muted-foreground">Statuts migrés depuis v{analysis.previousAnalysisId ? 'précédente' : ''} : </span>
              <span className="font-medium">{analysis.migratedItemsCount}</span>
            </div>
          )}
          {analysis.counts && (
            <div className="pt-2">
              <span className="text-muted-foreground">Compteurs : </span>
              <span className="font-medium">
                {analysis.counts.requirements} exigences ·{' '}
                {analysis.counts.profiles} profils ·{' '}
                {analysis.counts.requiredDocuments} pièces administratives
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {!isCurrent && (
        <div className="rounded-md border bg-muted/40 p-3 text-sm">
          <p>
            Cette analyse n'est plus courante. Les items affichés ci-dessous correspondent à son état au
            moment du remplacement. L'historique complet est conservé pour audit.
          </p>
        </div>
      )}

      {isCurrent && (
        <>
          <SummaryCard title="Exigences" total={requirements?.length} validated={requirements?.filter((r: DceRequirement) => r.humanStatus === 'VALIDATED').length} />
          <SummaryCard title="Profils consultants" total={profiles?.length} validated={profiles?.filter((p: DceProfile) => p.humanStatus === 'VALIDATED').length} />
          <SummaryCard title="Pièces administratives" total={requiredDocs?.length} validated={requiredDocs?.filter((d: DceRequiredDocument) => d.humanStatus === 'VALIDATED').length} />
        </>
      )}

      {history && history.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Autres analyses</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              {history
                .filter((h) => h.id !== analysis.id)
                .map((h) => (
                  <li key={h.id}>
                    <Link
                      to={`/tm/opportunities/${opportunityId}/analysis/history/${h.id}`}
                      className="text-copper hover:underline"
                    >
                      {new Date(h.createdAt).toLocaleDateString('fr-FR')} — {STATUS_LABELS[h.status]}
                      {h.isCurrent && ' (courante)'}
                    </Link>
                  </li>
                ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SummaryCard({
  title,
  total,
  validated,
}: {
  title: string;
  total: number | undefined;
  validated: number | undefined;
}) {
  if (total === undefined) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {title} ({total})
        </CardTitle>
      </CardHeader>
      <CardContent className="text-sm">
        {validated !== undefined && (
          <p>
            <span className="text-muted-foreground">Validés : </span>
            <span className="font-semibold">
              {validated} / {total}
            </span>
          </p>
        )}
      </CardContent>
    </Card>
  );
}
